import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  MenuItem,
  Chip,
  useMediaQuery,
  useTheme,
  Stack,
  InputAdornment,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import DataGrid from '~/components/General/OlcDataGrid';
import OlcActionMenu from '~/components/General/OlcActionMenu';
import { Add as AddIcon, MoreVert as MoreVertIcon, Close as CloseIcon } from '@mui/icons-material';
import { Input } from '@mui/material';

// Styles
import './styles.scss';
import {
  CompanySearchIcon,
  CompanyImageIcon,
  CompanyUploadIcon,
  GeneralPencilCIcon,
  GeneralCrossCIcon,
  CompanyDownloadIcon,
  CompanyTrashIcon,
  BrandingAssetsIcon,
  ModalTrashIcon,
  WarningErrorImport,
} from '~/assets/images';

// Components
import ConfirmModal from '~/components/General/ConfirmModal';
import OlcFormModal from '~/components/General/OlcFormModal';
import InlineEditInput from '~/components/General/InlineEditInput';
import { dateFormat } from '~/utils/date-format';
// Redux Actions
import {
  fetchBrandingAssets,
  createBrandingAsset,
  updateBrandingAsset,
  deleteBrandingAsset,
} from '~/redux/actions/branding-assets-actions';
import { success, failure } from '~/redux/actions/snackbar-actions';
import NoRecords from '~/components/General/NoRecords';

// Utils
import { debounce } from '~/utils/helperFunctions';
import TruncatedText from '~/components/General/TruncatedText';
import OlcButton from '~/components/General/OlcButton';
import OlcTablePagination from '~/components/General/OlcTablePagination';

const BrandingAssets = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useDispatch();

  // Redux state
  const brandingAssetsState = useSelector((state) => state.brandingAssetsReducers || {});

  const {
    brandingAssets = [],
    loading = false,
    rowsCount = 0,
    currentPage: reduxCurrentPage = 1,
  } = brandingAssetsState;

  const [searchTerm, setSearchTerm] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [activeRow, setActiveRow] = useState(null);
  const [activeRowId, setActiveRowId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [searchApplied, setSearchApplied] = useState(false);
  const [paginationLoading, setPaginationLoading] = useState(false);
  const [showNoRows, setShowNoRows] = useState(false);
  const [editingFileId, setEditingFileId] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [localUpdates, setLocalUpdates] = useState({});
  const pageSize = brandingAssetsState.perPage || 20;
  const pageSizes = [20, 50, 100];

  // Ensure brandingAssets is always an array and merge with local updates
  const safeBrandingAssets = useMemo(() => {
    const assets = Array.isArray(brandingAssets) ? brandingAssets : [];
    // Apply local updates to the assets
    return assets.map((asset) => {
      if (localUpdates[asset.id]) {
        return { ...asset, ...localUpdates[asset.id] };
      }
      return asset;
    });
  }, [brandingAssets, localUpdates]);

  // Fetch branding assets on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        await dispatch(
          fetchBrandingAssets({
            pagination: { page: currentPage, pageSize: pageSize },
          })
        );
      } catch (error) {
        console.error('Error fetching branding assets:', error);
      }
    };

    fetchData();
  }, [dispatch, currentPage, pageSize]);

  // useEffect to handle initial load completion
  useEffect(() => {
    if (!loading && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [loading, isInitialLoad]);

  // useEffect to handle search clearing
  useEffect(() => {
    if (!searchTerm && !isInitialLoad) {
      setSearchApplied(false);
      setSearchTerm('');
      dispatch(
        fetchBrandingAssets({
          pagination: { page: currentPage, pageSize: pageSize },
        })
      );
    }
  }, [searchTerm, isInitialLoad, dispatch, currentPage, pageSize]);

  // Reset pagination loading when new data arrives
  useEffect(() => {
    setPaginationLoading(false);
  }, [safeBrandingAssets]);

  // Add delay for showing no rows overlay
  useEffect(() => {
    let timeoutId;

    if (!(isInitialLoad || loading || paginationLoading) && safeBrandingAssets.length === 0) {
      // Show "No Results Found" after a delay when not loading and no data
      timeoutId = setTimeout(() => {
        setShowNoRows(true);
      }, 300); // 300ms delay
    } else {
      // Hide immediately when loading or when we have data
      setShowNoRows(false);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isInitialLoad, loading, paginationLoading, safeBrandingAssets.length]);

  // Reset inline editing when pagination changes
  useEffect(() => {
    if (editingFileId !== null) {
      setEditingFileId(null);
      setEditingValue('');
      setSaving(false);
    }
  }, [currentPage, brandingAssetsState.perPage]);

  // Clear local updates when data is refreshed (pagination, search, etc.)
  useEffect(() => {
    setLocalUpdates({});
  }, [currentPage, searchTerm, brandingAssetsState.perPage]);

  // Debug saving state
  useEffect(() => {
    console.log('BrandingAssets - saving state changed:', saving);
  }, [saving]);

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value.trimStart());
  };

  // Handle search execution
  const handleSearch = () => {
    if (searchTerm) {
      setCurrentPage(1);
      setSearchApplied(true);
      dispatch(
        fetchBrandingAssets({
          pagination: { page: 1, pageSize: pageSize },
          search: searchTerm,
        })
      );
    }
  };

  // Handle Enter key press in search field
  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Debounced functions
  const debouncedHandleKeyDown = debounce(handleSearchKeyPress, 300);
  const debouncedHandleSearch = debounce(handleSearch, 300);

  // Use the files directly from Redux state (server-side filtered)
  const currentPageFiles = safeBrandingAssets;

  // Calculate pagination - use Redux state for server-side pagination
  const totalPages = Math.ceil((brandingAssetsState.totalBrandingAssetsInDB || 0) / pageSize);
  const startIndex = ((brandingAssetsState.currentPage || 1) - 1) * pageSize;
  const endIndex = Math.min(
    startIndex + (brandingAssetsState.perPage || pageSize),
    brandingAssetsState.totalBrandingAssetsInDB || 0
  );
  const lastPage = brandingAssetsState.lastPage || 1;

  const handleMenuOpen = (event, file) => {
    setAnchorEl(event.currentTarget);
    setSelectedFile(file);
    setActiveRow(file);
    setActiveRowId(file);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    // Don't clear selectedFile and activeRow here - keep them for operations
  };

  const handleFileAction = (action) => {
    if (action === 'delete') {
      setDeleteModalOpen(true);
      handleMenuClose(); // Close the menu immediately when delete is clicked
    } else if (action === 'rename') {
      // Use inline editing instead of modal
      setEditingFileId(activeRowId.id);
      // Extract filename without extension
      const fileNameWithoutExtension = activeRowId.name.replace(/\.[^/.]+$/, '');
      setEditingValue(fileNameWithoutExtension);
      handleMenuClose();
    } else if (action === 'download') {
      handleDownload(activeRowId);
      handleMenuClose();
    } else {
      handleMenuClose();
    }
  };

  const handleInlineEdit = (file) => {
    setEditingFileId(file.id);
    // Extract filename without extension
    const fileNameWithoutExtension = file.name.replace(/\.[^/.]+$/, '');
    setEditingValue(fileNameWithoutExtension);
  };

  const handleInlineSave = async (newValue) => {
    const valueToSave = newValue || editingValue;
    if (!valueToSave.trim()) {
      dispatch(failure('File name cannot be empty'));
      return;
    }

    try {
      setSaving(true);

      // Find the original file to get its extension
      const originalFile = safeBrandingAssets.find((file) => file.id === editingFileId);
      if (!originalFile) {
        dispatch(failure('File not found'));
        setSaving(false);
        return;
      }

      // Extract extension from original filename
      const fileExtension = originalFile.name.match(/\.[^/.]+$/)?.[0] || '';

      // Concatenate new name with original extension
      const newFileNameWithExtension = valueToSave.trim() + fileExtension;

      // Pass the complete filename with extension to the API
      const response = await dispatch(updateBrandingAsset(editingFileId, newFileNameWithExtension));

      if (response && !response.error) {
        dispatch(success(response.data?.message || 'File renamed successfully'));

        // Update the local state immediately to show the change (like TagsListing)
        setLocalUpdates((prev) => ({
          ...prev,
          [editingFileId]: {
            name: newFileNameWithExtension,
          },
        }));

        setEditingFileId(null);
        setEditingValue('');
      } else {
        dispatch(failure(response?.data?.message || 'Failed to rename file'));
      }
    } catch (error) {
      dispatch(failure(error?.response?.data?.message || 'Failed to rename file'));
    } finally {
      setSaving(false);
    }
  };

  const handleInlineCancel = () => {
    setEditingFileId(null);
    setEditingValue('');
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setDeleteLoading(false);
    // Clear selectedFile and activeRow when modal closes
    setSelectedFile(null);
    setActiveRow(null);
    setActiveRowId(null);
  };

  const handleConfirmDelete = async () => {
    if (!activeRowId?.id) {
      dispatch(failure('No file selected for deletion'));
      return;
    }

    try {
      setDeleteLoading(true);
      const response = await dispatch(deleteBrandingAsset(activeRowId.id));

      if (response && !response.error) {
        dispatch(success(response.data?.message || 'File deleted successfully'));

        // Refresh the file list after successful deletion
        dispatch(
          fetchBrandingAssets({
            pagination: { page: currentPage, pageSize: pageSize },
            search: searchTerm || undefined,
          })
        );

        setDeleteModalOpen(false);
        // Clear selectedFile and activeRow after successful deletion
        setSelectedFile(null);
        setActiveRow(null);
        setActiveRowId(null);
      } else {
        dispatch(failure(response?.data?.message || 'Failed to delete file'));
      }
    } catch (error) {
      dispatch(failure(error?.response?.data?.message || 'Failed to delete file'));
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleCloseRenameModal = () => {
    setRenameModalOpen(false);
    handleMenuClose(); // Close the menu and clear selectedFile when modal closes
  };

  const handleRenameSuccess = () => {
    // Refresh the file list after successful rename
    dispatch(
      fetchBrandingAssets({
        pagination: { page: currentPage, pageSize: pageSize },
      })
    );
  };

  // Handle image preview
  const handleImagePreview = (file) => {
    setPreviewFile(file);
    setPreviewModalOpen(true);
  };

  // Handle preview modal close
  const handleClosePreviewModal = () => {
    setPreviewModalOpen(false);
    setPreviewFile(null);
  };

  // Handle file download
  const handleDownload = async (file) => {
    if (!file || !file.fileUrl) {
      console.error('File or fileUrl not available');
      return;
    }

    try {
      // Fetch the file as a blob
      const response = await fetch(file.fileUrl);
      if (!response.ok) {
        throw new Error('Failed to fetch file');
      }

      const blob = await response.blob();

      // Create object URL from blob
      const blobUrl = URL.createObjectURL(blob);

      // Create temporary anchor element for download
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = file.name;

      // Append to body, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the blob URL
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Error downloading file:', error);
      // Fallback: try simple download with download attribute
      try {
        const link = document.createElement('a');
        link.href = file.fileUrl;
        link.download = file.name;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (fallbackError) {
        // Last resort: open in new tab
        window.open(file.fileUrl, '_blank');
      }
    }
  };

  const handleChangePage = (event, newPage) => {
    setPaginationLoading(true);
    setCurrentPage(newPage);
    // Fetch data for the new page
    dispatch(
      fetchBrandingAssets({
        pagination: { page: newPage, pageSize: pageSize },
        search: searchTerm || undefined,
      })
    );
  };

  const handleChangePageSize = (event) => {
    setPaginationLoading(true);
    const newPageSize = event.target.value;
    setCurrentPage(1);
    // Fetch data with new page size
    dispatch(
      fetchBrandingAssets({
        pagination: { page: 1, pageSize: newPageSize },
        search: searchTerm || undefined,
      })
    );
  };

  const open = Boolean(anchorEl);

  // Define loader state - exclude loading when inline editing to avoid double loaders
  const showLoader = isInitialLoad || (loading && !saving) || paginationLoading;

  // DataGrid columns configuration
  const columns = [
    {
      field: 'name',
      headerName: 'Name',
      flex: editingFileId ? 0.7 : 0.4,
      minWidth: editingFileId ? 460 : 200,
      sortable: false,
      renderCell: (params) => {
        const isEditing = editingFileId === params.row.id;

        return (
          <Box
            sx={{
              display: 'flex',
              gap: '16px',
              alignItems: 'center',
              width: '100%',
              overflow: 'visible',
            }}
          >
            <img src={CompanyImageIcon} alt="file" style={{ width: '20px', height: '20px' }} />
            {isEditing ? (
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ flex: 1, minWidth: 0, overflow: 'visible' }}
              >
                <InlineEditInput
                  key={`edit-${editingFileId}`}
                  value={editingValue}
                  setValue={setEditingValue}
                  handleSave={handleInlineSave}
                  handleCancel={handleInlineCancel}
                  saving={saving}
                  maxLength={200}
                  errorMessage="*File name should be less than 200 characters"
                  shouldCancel={deleteModalOpen || addModalOpen}
                  autoSelect={false}
                  inputDynamicWidth={false}
                  defaultWidth={420}
                  maxWidth={420}
                  ariaLabel="Branding asset name"
                />
              </Stack>
            ) : (
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}
              >
                <div className="fileNameLink" style={{ cursor: 'pointer', flex: 1, minWidth: 0 }}>
                  <TruncatedText
                    key={`${params.row.id}-${params.row.name}`}
                    className={`fileNameLinkText ${editingFileId ? 'disabledText' : ''}`}
                    text={params.row.name}
                    onClickHandler={() => handleDownload(params.row)}
                    maxLength={30}
                  />
                </div>
              </Stack>
            )}
          </Box>
        );
      },
    },
    {
      field: 'type',
      headerName: 'Type',
      flex: 0.2,
      minWidth: 100,
      sortable: false,
      renderCell: (params) => <Typography className="fileType">{params.row.type}</Typography>,
    },
    {
      field: 'sizeFormatted',
      headerName: 'Size',
      flex: 0.2,
      minWidth: 100,
      sortable: false,
      renderCell: (params) => (
        <Typography className="fileType">{params.row.sizeFormatted}</Typography>
      ),
    },
    {
      field: 'createdAt',
      headerName: 'Date Added',
      flex: 0.3,
      minWidth: 130,
      sortable: false,
      renderCell: (params) => (
        <Typography className="fileType">{dateFormat(params.row.createdAt, true)}</Typography>
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 80,
      sortable: false,
      renderCell: (params) => {
        const isMenuOpen = open && activeRowId?.id === params.row.id;
        return (
          <Box sx={{ textAlign: 'right', width: '100%' }}>
            <IconButton
              aria-label={`Open actions for asset ${params.row.name || params.row.fileName}`}
              id={`branding-asset-actions-${params.row.id}`}
              aria-controls={isMenuOpen ? 'customfield-actions-menu' : undefined}
              aria-expanded={isMenuOpen ? 'true' : undefined}
              aria-haspopup="true"
              onClick={(event) => {
                setActiveRowId(params.row);
                handleMenuOpen(event, params.row);
              }}
              className="olcTableDropdownIcon"
              sx={{
                opacity: isMenuOpen ? '0.8 !important' : '0.4 !important',
              }}
            >
              <MoreVertIcon />
            </IconButton>
          </Box>
        );
      },
    },
  ];

  // File validation
  const validateFile = (file) => {
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/svg+xml'];
    const maxSize = 5 * 1024 * 1024; // 5MB

    if (!allowedTypes.includes(file.type)) {
      setUploadError('Error: Unsupported file format.');
      return false;
    }

    if (file.size > maxSize) {
      setUploadError('Error: File size must be less than 5MB.');
      return false;
    }

    setUploadError('');
    return true;
  };

  // Handle modal open/close
  const handleOpenModal = useCallback(() => {
    setAddModalOpen(true);
    setUploadFile(null);
    setUploadError('');
  }, []);

  const handleCloseModal = useCallback(() => {
    setAddModalOpen(false);
    setUploadFile(null);
    setDragActive(false);
    setUploadError('');
  }, []);

  // Handle file upload
  const handleUpload = useCallback(
    async (file) => {
      if (file) {
        try {
          const formData = new FormData();
          formData.append('image', file);

          const response = await dispatch(createBrandingAsset(formData));
          if (response && !response.error) {
            dispatch(success(response.data?.message || 'File uploaded successfully'));

            // Refresh the file list after successful upload
            dispatch(
              fetchBrandingAssets({
                pagination: { page: currentPage, pageSize: pageSize },
              })
            );
            handleCloseModal();
          } else {
            dispatch(failure(response?.data?.message || 'Failed to upload file'));
          }
        } catch (error) {
          console.error('Error uploading file:', error);
          dispatch(failure(error?.response?.data?.message || 'Failed to upload file'));
        }
      }
    },
    [dispatch, currentPage, pageSize, handleCloseModal]
  );

  // Handle drag events
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  // Handle drop
  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      if (e.dataTransfer.files && e.dataTransfer.files[0]) {
        const file = e.dataTransfer.files[0];
        if (validateFile(file)) {
          setUploadFile(file);
          // Auto-upload the file
          handleUpload(file);
        }
      }
    },
    [handleUpload]
  );

  // Handle file input change
  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (validateFile(file)) {
        setUploadFile(file);
        // Auto-upload the file
        handleUpload(file);
      }
    }
  };

  return (
    <Box
      className="companyProfileTableTabContent"
      sx={{ px: currentPageFiles.length === 0 && !loading ? '' : '16px' }}
    >
      {/* Header Section */}

      {/* Files List */}
      <div className="companyProfileTableBody">
        <Box
          className={`brandingAssetsHeader ${currentPageFiles.length === 0 && !searchApplied ? 'isEmpty' : ''}`}
        >
          {(currentPageFiles.length > 0 || searchApplied) && (
            <Box className="searchWrapper olcSearchWrapper">
              <Input
                className={`searchInput ${searchApplied ? 'searchApplied' : ''}`}
                type="text"
                value={searchTerm}
                onChange={handleSearchChange}
                onKeyDown={debouncedHandleKeyDown}
                placeholder="Search by name"
                title="Search by name"
                endAdornment={
                  <InputAdornment position="end" className="searchInputAdornment">
                    <IconButton
                      aria-label="search"
                      edge="end"
                      className="searchIcon"
                      onClick={debouncedHandleSearch}
                    >
                      <img src={CompanySearchIcon} alt="search" />
                    </IconButton>
                    {searchTerm?.trim() && (
                      <IconButton
                        className="cpClearInputBtn"
                        size="small"
                        onClick={() => {
                          setSearchTerm('');
                          setSearchApplied(false);
                        }}
                        edge="end"
                        sx={{ padding: '4px' }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    )}
                  </InputAdornment>
                }
              />
            </Box>
          )}

          <div className="cpAddNewButton">
            <OlcButton variant="primary" onClick={handleOpenModal}>
              Add New
            </OlcButton>
          </div>
        </Box>

        {currentPageFiles.length === 0 && !loading && !searchApplied && !isInitialLoad ? (
          <Stack
            className="noRecordsWrapperHere"
            style={{ marginTop: '20px', padding: '0px 16px 40px 16px' }}
          >
            <NoRecords
              icon={
                <Box style={{ width: '40px', height: '40px' }}>
                  <img
                    src={BrandingAssetsIcon}
                    style={{ width: '33.33px', height: '33.33px' }}
                    alt="file"
                  />
                </Box>
              }
              title="No Branding Assets Yet"
              linkText="Learn more"
              linkUrl="https://app.gitbook.com/o/CC91xggjWSs9WowYNbKb/s/SP96nBmkaaFJC8FJquKi/~/changes/76/product-and-order-guide/creating-a-template"
              message="Upload logos, images, and other brand elements to ensure your mail pieces match your company's
              look and feel. Keep assets organized so they're easy to apply across campaigns."
              messageWidth="70%"
            />
          </Stack>
        ) : (
          <>
            <Box
              className={`olcTableGridInternal ${showLoader ? 'table-loading' : ''} ${safeBrandingAssets.length === 0 ? 'table-empty' : ''}`}
            >
              <Box sx={{ position: 'relative' }}>
                <DataGrid
                  rows={currentPageFiles}
                  columns={columns}
                  fillAvailableWidth
                  getRowId={(row) => row.id}
                  disableColumnMenu
                  disableRowSelectionOnClick
                  pagination={false}
                  rowHeight={61}
                  loading={showLoader}
                  sx={{
                    width: '100%',
                    '& .MuiDataGrid-cell': {
                      overflow: 'visible !important',
                    },
                    '& .MuiDataGrid-columnHeader': {
                      overflow: 'visible !important',
                    },
                    ...(showNoRows ||
                    (!(isInitialLoad || loading || paginationLoading) &&
                      safeBrandingAssets.length === 0)
                      ? {
                          height: 'auto',
                          minHeight: '61px',
                        }
                      : {}),
                  }}
                  getRowClassName={(params) => (params.id === editingFileId ? 'row-editing' : '')}
                  slots={{
                    noRowsOverlay: () =>
                      showNoRows ? (
                        <Typography className="noResultsText">No Results Found</Typography>
                      ) : null,
                    noResultsOverlay: () =>
                      showNoRows ? (
                        <Typography className="noResultsText">No Results Found</Typography>
                      ) : null,
                  }}
                />
              </Box>
            </Box>
          </>
        )}
      </div>

      {/* Pagination */}
      {(showLoader || rowsCount > 0) && (
        <OlcTablePagination
          count={lastPage}
          page={brandingAssetsState.currentPage || 1}
          pageSize={brandingAssetsState.perPage || pageSize}
          pageSizes={pageSizes}
          onPageChange={handleChangePage}
          onPageSizeChange={handleChangePageSize}
          from={brandingAssetsState.from || 0}
          to={
            rowsCount < (brandingAssetsState.to || 0)
              ? safeBrandingAssets.length + (brandingAssetsState.from || 0) - 1
              : brandingAssetsState.to || 0
          }
          total={rowsCount}
          loading={showLoader}
        />
      )}

      {/* Menu Actions */}
      <OlcActionMenu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={() => handleFileAction('rename')} disabled={editingFileId !== null}>
          <img src={GeneralPencilCIcon} alt="edit" />
          Rename
        </MenuItem>
        <MenuItem onClick={() => handleFileAction('download')}>
          <img src={CompanyDownloadIcon} alt="download" />
          Download
        </MenuItem>
        <MenuItem onClick={() => handleFileAction('delete')}>
          <img src={CompanyTrashIcon} alt="delete" />
          Delete
        </MenuItem>
      </OlcActionMenu>

      {/* Add New File Modal */}
      <OlcFormModal
        open={addModalOpen}
        title="Add New File"
        className="brandingAssetsModalWrapper"
        onClose={(event, reason) => {
          if (reason !== 'backdropClick') {
            handleCloseModal();
          }
        }}
      >
        <Box
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          sx={{
            border: '1px dashed rgba(0, 0, 0, 0.25)',
            borderRadius: '10px',
            padding: '40px 56px',
            textAlign: 'center',
            backgroundColor: '#F9FAFB',
            transition: 'all 0.3s ease',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            position: 'relative',
            gap: '24px',
          }}
        >
          <Box
            style={{
              width: '48px',
              height: '48px',
            }}
          >
            <img
              src={CompanyUploadIcon}
              alt="upload"
              style={{
                width: '48px',
                height: '48px',
              }}
            />
          </Box>

          <Box>
            <Typography
              variant="h6"
              sx={{
                marginBottom: '12px',
                color: '#000',
                fontWeight: 400,
                fontSize: '20px',
                lineHeight: '1.4',
              }}
            >
              Select a file or drag and drop here
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: 'rgba(0, 0, 0, 0.60)',
                fontSize: '16px',
                fontWeight: 400,
                lineHeight: 'normal',
              }}
            >
              JPG, PNG or SVG file size no more than 5MB
            </Typography>
          </Box>

          <input
            type="file"
            accept=".jpg,.jpeg,.png,.svg"
            onChange={handleFileInput}
            style={{ display: 'none' }}
            id="file-upload"
            disabled={loading}
          />

          <OlcButton
            variant="primary"
            className="cpfileUploadBtn"
            component="label"
            htmlFor="file-upload"
            disabled={loading}
            sx={{
              '&.Mui-disabled': {
                backgroundColor: 'rgba(237, 92, 47, 0.4) !important',
                color: '#fff !important',
              },
            }}
          >
            {loading ? (
              <>
                <CircularProgress size={16} sx={{ color: '#fff', mr: '5px' }} /> Uploading
              </>
            ) : (
              'Select File'
            )}
          </OlcButton>
        </Box>

        {uploadError && (
          <Box className="uploadError">
            <img src={WarningErrorImport} alt="warning" />
            <Typography
              sx={{
                color: '#e10606',
                fontFamily: 'Inter',
                fontSize: '14px',
                fontWeight: 400,
                textAlign: 'center',
              }}
            >
              {uploadError}
            </Typography>
          </Box>
        )}
      </OlcFormModal>

      {/* Delete File Modal */}
      <ConfirmModal
        openConfirmModal={{
          open: deleteModalOpen,
          header: 'Delete File',
          message: (
            <>
              Are you sure you want to delete this file? <br /> It will remain linked to existing
              templates but won’t be available for new ones.
            </>
          ),
          // subMessage: 'This action cannot be undone.',
          icon: ModalTrashIcon,
          className: 'deleteModal',
          successButtonName: 'Yes',
          handleOk: handleConfirmDelete,
          handleClose: handleCloseDeleteModal,
          loading: deleteLoading,
        }}
      />

      {/* Image Preview Modal */}
      <Dialog
        open={previewModalOpen}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '8px',
            maxHeight: '90vh',
            backgroundColor: '#f5f5f5',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px 24px',
            backgroundColor: '#fff',
            borderBottom: '1px solid #e0e0e0',
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#333' }}>
            {previewFile?.name || 'Image Preview'}
          </Typography>
          <IconButton
            onClick={handleClosePreviewModal}
            sx={{
              color: '#666',
              '&:hover': {
                backgroundColor: '#f5f5f5',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            padding: '24px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px',
            backgroundColor: '#f5f5f5',
          }}
        >
          {previewFile && previewFile.fileUrl && (
            <Box
              sx={{
                maxWidth: '100%',
                maxHeight: '70vh',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <img
                src={previewFile.fileUrl}
                alt={previewFile.name}
                style={{
                  maxWidth: '100%',
                  maxHeight: '70vh',
                  objectFit: 'contain',
                  borderRadius: '4px',
                  boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />
              <Typography
                variant="body1"
                sx={{
                  display: 'none',
                  textAlign: 'center',
                  color: '#666',
                  padding: '40px',
                }}
              >
                Unable to load image preview
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            padding: '16px 24px',
            backgroundColor: '#fff',
            borderTop: '1px solid #e0e0e0',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="body2" sx={{ color: '#666' }}>
            {previewFile && (
              <>
                Type: {previewFile.type} • Size: {previewFile.sizeFormatted} • Added:{' '}
                {new Date(previewFile.createdAt).toLocaleDateString()}
              </>
            )}
          </Typography>
          <OlcButton
            variant="primary"
            onClick={() => handleDownload(previewFile)}
            sx={{
              backgroundColor: '#ed5c2f',
              color: 'white',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: '#dd5026',
              },
            }}
          >
            Download
          </OlcButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BrandingAssets;
